import type { ScriptStep } from '@mytaverse-shared/interfaces/assistantScripts';
import type { PollyLanguage } from '@mytaverse-shared/interfaces/polly';

export type TranslateScope = 'steps' | 'button';

// A language counts as already translated (for the given scope) when at least
// one translatable item is non-empty — a partial translation still counts as
// existing.
export const langHasTranslation = (
  scope: TranslateScope,
  lang: PollyLanguage,
  scriptSteps: ScriptStep[],
  uiClickButtonLabelTranslations: Partial<Record<PollyLanguage, string>>,
): boolean =>
  scope === 'button'
    ? (uiClickButtonLabelTranslations[lang] ?? '').trim().length > 0
    : scriptSteps.some((s) => (s.translations?.[lang] ?? '').trim().length > 0);

// Splits the requested target languages into the ones that already have a
// translation (would be overwritten) and the ones that are still empty.
// `needsPrompt` is true when at least one target already has a translation, in
// which case the caller should ask the user before overwriting.
export const splitTranslationTargets = (
  scope: TranslateScope,
  targetLangs: PollyLanguage[],
  scriptSteps: ScriptStep[],
  uiClickButtonLabelTranslations: Partial<Record<PollyLanguage, string>>,
): {
  existing: PollyLanguage[];
  empty: PollyLanguage[];
  needsPrompt: boolean;
} => {
  const has = (lang: PollyLanguage) =>
    langHasTranslation(
      scope,
      lang,
      scriptSteps,
      uiClickButtonLabelTranslations,
    );

  const existing = targetLangs.filter(has);
  const empty = targetLangs.filter((l) => !has(l));

  return { existing, empty, needsPrompt: existing.length > 0 };
};
